import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window';
import PropTypes from 'prop-types';
import {
    Box,
    InputAdornment,
    TextField,
    Typography,
    Button,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Pagination,
    Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import throttle from 'lodash.throttle';
import useField from '../../hooks/useField';
import SearchListItem from './SearchListItem';
import dayjs from 'dayjs';
import 'dayjs/locale/fi';
import MuotoiltuListItem from './MuotoiltuListItem';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../features/authentication/authSlice';
import HelpIconWithTooltip from '../HelpIconWithTooltip';

const SearchList = ({ data, title, color, listDimensions, primaryPropKey, secondaryPropKey, noPagination, muotoiltu, filterTime, deleteEvent, showPast, hideStatusFilter = false, pageSize = 15 }) => {
    const { setValue, ...searchField } = useField('text');
    const [currentPage, setCurrentPage] = useState(0);
    const [showCurrentAndFuture, setShowCurrentAndFuture] = useState(true);
    const [showMuotoiltu, setShowMuotoiltu] = useState(false);
    const [isTulevaOrMennyt, setIsTulevaOrMennyt] = useState(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');

    const { role } = useSelector(selectCurrentUser);

    const currentDate = dayjs();
    const deferredSearchValue = useDeferredValue(searchField.value);
    const searchTerm = deferredSearchValue?.replace(/\s/g, '').toLowerCase();

    const statusOptions = ['julkaistu', 'peruutettu', 'raportoitu', 'esitiedot', 'laskutuksessa', 'laskutettu'];

    const filteredData = useMemo(() => {
        let nextData = data ?? [];

        if (filterTime) {
            nextData = showPast
                ? nextData.filter((item) => {
                    const eventDate = dayjs(item.aloitus, 'DD.MM.YYYY HH:mm').locale('fi');
                    return eventDate.isBefore(currentDate);
                }).sort((a, b) => {
                    const eventA = dayjs(a.aloitus, 'DD.MM.YYYY HH:mm').valueOf();
                    const eventB = dayjs(b.aloitus, 'DD.MM.YYYY HH:mm').valueOf();
                    return eventB - eventA;
                })
                : (showCurrentAndFuture || showMuotoiltu)
                    ? nextData.filter((item) => {
                        const eventLopetusDate = dayjs(item.lopetus, 'DD.MM.YYYY HH:mm').locale('fi');
                        const eventAloitusDate = dayjs(item.aloitus, 'DD.MM.YYYY HH:mm').locale('fi');
                        return !eventLopetusDate.isBefore(currentDate) || (eventAloitusDate.isBefore(currentDate) && !eventLopetusDate.isBefore(currentDate));
                    }).sort((a, b) => {
                        const eventA = dayjs(a.aloitus, 'DD.MM.YYYY HH:mm').valueOf();
                        const eventB = dayjs(b.aloitus, 'DD.MM.YYYY HH:mm').valueOf();
                        return eventA - eventB;
                    })
                    : nextData.filter((item) => {
                        const eventDate = dayjs(item.aloitus, 'DD.MM.YYYY HH:mm').locale('fi');
                        return eventDate.isBefore(currentDate);
                    }).sort((a, b) => {
                        const eventA = dayjs(a.aloitus, 'DD.MM.YYYY HH:mm').valueOf();
                        const eventB = dayjs(b.aloitus, 'DD.MM.YYYY HH:mm').valueOf();
                        return eventB - eventA;
                    });
        }

        if (!hideStatusFilter && statusFilter) {
            nextData = nextData?.filter((item) => item.status === statusFilter);
        }

        if (searchTerm) {
            nextData = nextData?.filter((item) => {
                const primaryProp = item[primaryPropKey].replace(/\s/g, '').toLowerCase();
                return primaryProp.includes(searchTerm.toLowerCase());
            });
        }

        return nextData;
    }, [currentDate, data, filterTime, hideStatusFilter, primaryPropKey, searchTerm, showCurrentAndFuture, showMuotoiltu, showPast, statusFilter]);

    const totalPages = Math.ceil(filteredData.length / pageSize);

    const handleScroll = throttle(() => {
        // Handle scroll event
    }, 100);

    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    const dataForPage = filteredData.slice(startIndex, endIndex);
    const visibleStart = filteredData.length === 0 ? 0 : startIndex + 1;
    const visibleEnd = noPagination ? filteredData.length : Math.min(endIndex, filteredData.length);

    useEffect(() => {
        setCurrentPage(0);
    }, [data, searchTerm, statusFilter, showCurrentAndFuture, showMuotoiltu, showPast]);

    useEffect(() => {
        if (currentPage > 0 && currentPage >= totalPages) {
            setCurrentPage(Math.max(totalPages - 1, 0));
        }
    }, [currentPage, totalPages]);

    let content;

    const handleMuotoiltuListausButtonClick = () => {
        setShowMuotoiltu(true);
        setShowCurrentAndFuture(false);
        setIsTulevaOrMennyt(false);
    };

    const handleTulevatButtonClick = () => {
        setShowCurrentAndFuture(true);
        setCurrentPage(0);
        setShowMuotoiltu(false);
        setIsTulevaOrMennyt(true);
    };

    const handleMenneetButtonClick = () => {
        setShowCurrentAndFuture(false);
        setCurrentPage(0);
        setShowMuotoiltu(false);
        setIsTulevaOrMennyt(true);
    };

    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setCurrentPage(0);
    };

    const handleOpenSnackbar = (message) => {
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    const muotoiltuListDimensions = { height: 350, itemSize: 100 };

    if (!filteredData.length)
        content = (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    height: '100%',
                    pt: { xs: 5, md: 7 },
                }}
            >
                <Typography variant='overline' textAlign='center'>
                    Ei näytettäviä tuloksia
                </Typography>
            </Box>
        );
    else
        content = (
            <>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                    {filterTime && (role !== 'Laskuttaja' || !showPast) ? (
                        <>
                            <Button
                                key="future"
                                variant={(showCurrentAndFuture && isTulevaOrMennyt && !showMuotoiltu) ? 'contained' : 'outlined'}
                                onClick={handleTulevatButtonClick}
                                sx={{ marginX: '4px' }}
                            >
                                Tulevat
                            </Button>
                            <Button
                                key="past"
                                variant={(!showCurrentAndFuture && isTulevaOrMennyt && !showMuotoiltu) ? 'contained' : 'outlined'}
                                onClick={handleMenneetButtonClick}
                                sx={{ marginX: '4px' }}
                            >
                                Menneet
                            </Button>
                        </>
                    ) : null}
                    {muotoiltu && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                variant={!showMuotoiltu ? 'outlined' : 'contained'}
                                onClick={handleMuotoiltuListausButtonClick}
                                sx={{ marginX: '4px' }}
                            >
                                Muotoiltu listaus
                            </Button>
                            <HelpIconWithTooltip
                                iconSize={20}
                                tooltipFontSize="16px"
                                text="Muotoiltu listaus mahdollistaa tapahtuman klikkaamisen ja sen tietojen helpon saamisen leikepöydälle"
                            />
                        </div>
                    )}
                </div>
                {!hideStatusFilter && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel id="status-filter-label">Tapahtuman status</InputLabel>
                            <Select
                                labelId="status-filter-label"
                                value={statusFilter}
                                label="Tapahtuman status"
                                onChange={handleStatusFilterChange}
                            >
                                <MenuItem value="">Kaikki</MenuItem>
                                {statusOptions.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                )}
                {isTulevaOrMennyt ? (
                    <FixedSizeList
                        {...listDimensions}
                        itemCount={noPagination ? filteredData.length : dataForPage.length}
                        overscanCount={4}
                        onScroll={handleScroll}
                        itemData={noPagination ? filteredData : dataForPage}
                    >
                        {({ data, index, style }) => (
                            <SearchListItem
                                deleteEvent={deleteEvent}
                                data={data[index]}
                                key={data.id}
                                style={style}
                                searchTerm={searchField.value}
                                primaryPropKey={primaryPropKey}
                                secondaryPropKey={secondaryPropKey}
                            />
                        )}
                    </FixedSizeList>
                ) : (
                    <>
                        <FixedSizeList
                            {...muotoiltuListDimensions}
                            itemCount={filteredData.length}
                            overscanCount={4}
                            onScroll={handleScroll}
                            itemData={filteredData}
                        >
                            {({ data, index, style }) => (
                                <MuotoiltuListItem
                                    handleOpenSnackbar={handleOpenSnackbar}
                                    data={data[index]}
                                    key={index}
                                    style={style}
                                />
                            )}
                        </FixedSizeList>
                        <Snackbar
                            open={snackbarOpen}
                            autoHideDuration={6000}
                            onClose={handleCloseSnackbar}
                            message="Kopioitu leikepöydälle"
                            action={
                                <Button color="inherit" size="small" onClick={handleCloseSnackbar}>
                                    Sulje
                                </Button>
                            }
                        />
                    </>
                )}
            </>
        );

    const goToPage = (page) => {
        setCurrentPage(page);
    };

    return (
        <Stack
            spacing={2}
            sx={{
                backgroundColor: color,
                borderRadius: 3,
                width: '100%',
                height: { xs: 'auto', md: '100%' },
                minHeight: { xs: 340, md: 520 },
                p: 2,
                overflow: 'hidden',
            }}
        >
            <Typography fontFamily='"Bree Serif", serif' textAlign='center' variant='h6'>
                {title}
            </Typography>
            <Box display='flex' justifyContent='center'>
                <TextField
                    {...searchField}
                    variant='standard'
                    placeholder='Hae'
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                sx={{
                                    color: 'taphaBlue.main',
                                    pr: 1,
                                }}
                                position='start'
                            >
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            <Box
                sx={{
                    textAlign: 'left',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflow: 'hidden',
                }}
            >
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent='space-between'
                    spacing={0.5}
                    sx={{ mb: 1.5 }}
                >
                </Stack>
                <Stack sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden', justifyContent: 'flex-start' }}>
                    {content}
                </Stack>
                {noPagination !== true && (
                    <Stack alignItems='center' spacing={1} sx={{ mt: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={Math.min(currentPage + 1, Math.max(totalPages, 1))}
                            onChange={(_, page) => goToPage(page - 1)}
                            color='primary'
                            shape='rounded'
                            siblingCount={1}
                            boundaryCount={1}
                            size='small'
                            showFirstButton
                            showLastButton
                        />
                    </Stack>
                )}
            </Box>
        </Stack>
    );
};

SearchList.propTypes = {
    data: PropTypes.array,
    title: PropTypes.string,
    color: PropTypes.string,
    listDimensions: PropTypes.object,
    primaryPropKey: PropTypes.string,
    secondaryPropKey: PropTypes.string,
    noPagination: PropTypes.bool,
    muotoiltu: PropTypes.bool,
    filterTime: PropTypes.bool,
    deleteEvent: PropTypes.func,
    showPast: PropTypes.bool,
    hideStatusFilter: PropTypes.bool,
    pageSize: PropTypes.number
};

export default SearchList;